import { matchPath } from 'react-router-dom';

import { get as getSetting } from '../helpers/settings';

type Params = Record<string, string | undefined>;

// admin-ui opens a detail page in create mode with the id "new"; Dealer uses "-"
const detailId = (id?: string) => (id === 'new' ? '-' : id);

// Admin is moving to the admin section of Dealer (dealer-ui, /admin). Each admin-ui route maps
// to the Dealer page that replaces it, or to null when Dealer has no counterpart yet. More
// specific patterns come first, since the first match wins.
const routeMap: [pattern: string, toDealerPath: (params: Params) => string | null][] = [
  ['/', () => '/admin'],
  ['/financing-providers/:id', ({ id }) => `/admin/financing-providers/${detailId(id)}`],
  ['/financing-providers', () => '/admin/financing-providers'],
  ['/settings', () => '/admin/templates/market-settings'],
  ['/accessories/:id', ({ id }) => `/admin/templates/accessories/${detailId(id)}`],
  ['/accessories', () => '/admin/templates/accessories'],
  ['/websites', () => '/admin/websites'],
  ['/synonyms/*', () => '/admin/search/synonyms'],
  ['/administrators/*', () => '/admin/access/administrators'],
  ['/branches/:id/groups', ({ id }) => `/admin/branches/${id}/branches`],
  ['/branches/:id/assignment-options', ({ id }) => `/admin/branches/${id}/assignments`],
  ['/branches/:id/import-status', () => null],
  ['/branches/:id/equipment-settings', () => null],
  // The archive tab lives in the danger zone of the branch's overview in Dealer
  ['/branches/:id/*', ({ id }) => `/admin/branches/${id}`],
  ['/branches', () => '/admin/branches'],
  ['/permissions', () => '/admin/access/memberships'],
  ['/users', () => '/admin/access/users'],
  ['/wxr-boost', () => '/admin/visibility/wxr'],
  ['/system-clients', () => '/admin/access/system-clients'],
  ['/unarchive', () => null],
  ['/blocket-view', () => '/admin/blocket/blocket-view'],
  ['/blocket-outbox-stats', () => '/admin/blocket/blocket-outbox-stats'],
  [
    '/vehicle-view/:type/:vehicle',
    ({ vehicle }) => `/admin/vehicles/vehicle-view?q=${encodeURIComponent(vehicle ?? '')}`,
  ],
  ['/vehicle-view', () => '/admin/vehicles/vehicle-view'],
  ['/virtual-vehicle/:id', ({ id }) => `/admin/templates/virtual-vehicle/${detailId(id)}`],
  ['/virtual-vehicle', () => '/admin/templates/virtual-vehicle'],
  ['/bankid', () => '/admin/bankid'],
  ['/assignment-options', () => '/admin/assignment-options'],
  ['/profile', () => '/admin/access/profile'],
  ['/seo/:id', ({ id }) => `/admin/visibility/seo/${detailId(id)}`],
  ['/seo', () => '/admin/visibility/seo'],
  ['/mass-deletion-protection/:id', ({ id }) => `/admin/vehicles/mass-deletion-protection/${id}`],
  ['/mass-deletion-protection', () => '/admin/vehicles/mass-deletion-protection'],
  ['/alert-banners', () => '/admin/alert-banners'],
];

export type DealerAdminLink =
  | { status: 'available'; url: string }
  // The page has no counterpart in Dealer yet, so it is still used here
  | { status: 'unavailable' };

/**
 * Where the admin-ui page at `pathname` lives in Dealer. Null for routes that aren't admin pages
 * (such as the sign-in callback) or when the Dealer URL isn't configured.
 */
export const getDealerAdminLink = (pathname: string): DealerAdminLink | null => {
  const dealerUiUri = getSetting('dealerUiUri');
  if (!dealerUiUri) return null;

  for (const [pattern, toDealerPath] of routeMap) {
    const match = matchPath(pattern, pathname);
    if (match) {
      const dealerPath = toDealerPath(match.params);
      return dealerPath
        ? { status: 'available', url: `${dealerUiUri.replace(/\/$/, '')}${dealerPath}` }
        : { status: 'unavailable' };
    }
  }

  return null;
};
